/**
 * A página da Ordem de Pagamento de um cartão de Outros Bancos, aberta numa aba
 * nova.
 *
 * Papel: reproduz o documento de `gerarOrdemPagamento` (l. 3834–3918) como
 * página: "A débito de", os dados bancários do favorecido, a discriminação, o
 * total a pagar com o valor por extenso e a data, montados a partir de `GET
 * financas/documentos/ordem-de-pagamento`, e a impressão chamada assim que a
 * ordem chega.
 *
 * Como usar: o `router.tsx` a monta em
 * `/financas/ordem-de-pagamento/:favorecidoId/:competencia`, dentro do
 * `DocumentoProtegido`, sem o `Layout`. A discriminação traz o salário líquido
 * e a ajuda de custo com valor, e nunca a guia DAE; depois de "Fechar Folha" o
 * salário volta a nulo, e a ordem sai só com a ajuda de custo.
 *
 * A recusa da API (4xx) — a ordem sem item, o favorecido que não existe, o 404,
 * a falta de permissão — mostra a frase dela no lugar da ordem, sem "Tentar de
 * novo", que nunca daria certo. Só a falha do servidor (5xx) ou da rede oferece
 * tentar de novo.
 */
import { type OrdemDePagamento } from '@concierge/shared';
import { type ReactNode } from 'react';
import { useParams } from 'react-router';

import { ErroDaApi } from '@/shared/api';
import { formatarCpf } from '@/shared/lib/formato';
import { valorPorExtenso } from '@/shared/lib/extenso';
import {
  AvisoDoDocumento,
  FolhaDoDocumento,
  ItensDoDocumento,
  TopoDoDocumento,
} from '../components/documento';
import { dataPorExtenso, referenteA } from '../documentos';
import { formatarReais } from '../formato';
import { useOrdemDePagamento } from '../hooks/usar-financas-extras';
import { useDocumentoPronto } from '../hooks/use-documento-pronto';

/**
 * Um bloco da ordem, com o título em caixa alta.
 *
 * @param titulo - "A débito de", "Favorecido" ou "Discriminação".
 * @param children - As linhas do bloco.
 * @returns O bloco, com 20 px abaixo.
 */
function BlocoDaOrdem({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="mb-5">
      <div className="mb-1.5 font-documento-rotulo text-[9.5px] tracking-[0.1em] text-emb-doc-rotulo-claro uppercase">
        {titulo}
      </div>
      {children}
    </div>
  );
}

/**
 * Uma linha de campo, com o pontilhado embaixo.
 *
 * @param rotulo - O nome do campo.
 * @param valor - O valor; nulo ou vazio aparece como "—".
 * @returns A linha.
 */
function LinhaDaOrdem({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  return (
    <div className="flex justify-between border-b border-dotted border-emb-doc-pontilhado-campo py-[3px] text-[13px]">
      <span className="text-emb-doc-texto-fraco">{rotulo}</span>
      <span className="font-semibold">{valor === null || valor === '' ? '—' : valor}</span>
    </div>
  );
}

/**
 * O documento da ordem.
 *
 * @param ordem - A ordem.
 * @returns Os três blocos, o total e a data.
 */
function DocumentoDaOrdem({ ordem }: { ordem: OrdemDePagamento }) {
  const { favorecido } = ordem;
  return (
    <FolhaDoDocumento className="px-[16mm] py-[18mm]">
      <TopoDoDocumento
        titulo="Ordem de Pagamento"
        referencia={referenteA(ordem.competencia)}
        className="mb-[26px]"
      />
      <BlocoDaOrdem titulo="A débito de">
        <LinhaDaOrdem rotulo="Nome" valor={ordem.debitoDe} />
      </BlocoDaOrdem>
      <BlocoDaOrdem titulo="Favorecido">
        <LinhaDaOrdem rotulo="Nome" valor={favorecido.nome} />
        <LinhaDaOrdem
          rotulo="CPF"
          valor={favorecido.cpf === null ? null : formatarCpf(favorecido.cpf)}
        />
        <LinhaDaOrdem rotulo="Banco" valor={favorecido.banco} />
        <LinhaDaOrdem rotulo="Agência" valor={favorecido.agencia} />
        <LinhaDaOrdem
          rotulo={`Conta ${favorecido.tipoConta ?? ''}`}
          valor={favorecido.conta}
        />
      </BlocoDaOrdem>
      <BlocoDaOrdem titulo="Discriminação">
        <ItensDoDocumento itens={ordem.itens} className="mb-5" />
      </BlocoDaOrdem>
      <div className="mb-1.5 flex items-baseline justify-between border-t-[1.5px] border-emb-doc-traco py-2.5">
        <span>Total a pagar</span>
        <span className="text-[17px] font-bold">{formatarReais(ordem.total)}</span>
      </div>
      <div className="mb-9 text-[11px] text-emb-doc-texto-fraco">
        {valorPorExtenso(ordem.total)}
      </div>
      <div className="my-5">{dataPorExtenso(ordem.data)}</div>
    </FolhaDoDocumento>
  );
}

/**
 * Página da ordem de pagamento.
 *
 * @returns A ordem, ou a linha de espera ou de falha.
 */
export function PaginaOrdemDePagamento() {
  const { favorecidoId = '', competencia = '' } = useParams();
  const ordem = useOrdemDePagamento({ favorecidoId, competencia });
  useDocumentoPronto(
    ordem.data === undefined ? null : `Ordem de Pagamento — ${ordem.data.favorecido.nome}`,
  );

  if (ordem.data === undefined) {
    const erro = ordem.error;
    if (erro instanceof ErroDaApi && erro.status >= 400 && erro.status < 500) {
      return <AvisoDoDocumento texto={erro.message} />;
    }
    return ordem.isError ? (
      <AvisoDoDocumento
        texto="Não foi possível carregar."
        onTentarDeNovo={() => void ordem.refetch()}
      />
    ) : (
      <AvisoDoDocumento texto="Carregando…" />
    );
  }
  return <DocumentoDaOrdem ordem={ordem.data} />;
}
